import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicNav } from '../../components/nav/PublicNav';
import { BrandInquiry } from '../../components/BrandInquiry';
import { Modal } from '../../components/Modal';
import './landing.css';

// Real Orbis campaign clips. Assets live in public/video/campaigns/.
// View counts are real and fixed; update them only with verified data.
const SHOWCASE = [
  { src: 'hero-42m', handle: 'Hero campaign', views: '42M' },
  { src: 'maiko-shakira-8m', handle: 'Maiko x Shakira', views: '8M' },
  { src: 'cortis-3m', handle: 'Cortis', views: '3M' },
  { src: 'cardi-melaxin', handle: 'Cardi x Melaxin', views: '1.6M' },
];

// Proof bar: real traction. "Thousands paid" and "Over 1B views" are
// founder-confirmed all-campaigns figures (Kevin, 2026-05-14). Per the
// no-fabrication rule, only ever update these to figures that stay true.
const PROOF = [
  { k: 'Thousands', v: 'already paid to clippers' },
  { k: 'Over 1B', v: 'views driven' },
  { k: 'Per-view', v: 'payouts, no flat fees' },
];

const STEPS = [
  {
    n: '1',
    h: 'Claim a bounty',
    p: 'Browse open clip bounties. Each one shows the source video, the payout rate, and the platforms it is open for. Claim the ones you want.',
  },
  {
    n: '2',
    h: 'Post your clip',
    p: 'Re-edit the source into your style and post it on your own account. Drop the link back in Studio. Every clip carries a serial we track across platforms.',
  },
  {
    n: '3',
    h: 'Get paid per verified view',
    p: 'We verify views hourly and credit your balance. Hit $100 and you cash out, paid within 45 days of month-end.',
  },
];

export function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('studio-night');
    return () => document.body.classList.remove('studio-night');
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* 1 — Nav */}
      <PublicNav theme="night" onDarkHero={!scrolled} />

      {/* 2 — Split hero: two equal paths (creators + brands) */}
      <header className="ln-hero">
        <div className="ln-wrap ln-hero-split">
          <div className="ln-hero-col reveal">
            <div className="ln-eyebrow">For creators</div>
            <h1>
              Turn the views you make into <span className="ln-hl">income.</span>
            </h1>
            <p className="ln-sub">
              Claim a clip bounty, post it on your account, and get paid for every verified
              view. No follower minimum.
            </p>
            <div className="ln-ctas">
              <Link to="/clipper" className="ln-btn-primary">
                Start clipping
              </Link>
              <button type="button" className="ln-btn-ghost" onClick={() => setDemoOpen(true)}>
                For brands
              </button>
            </div>
            <div className="ln-hero-mini">
              <span>
                <b>Per-view</b> payouts
              </span>
              <span>
                <b>$100</b> to cash out
              </span>
              <span>
                <b>Every view</b> verified
              </span>
            </div>
          </div>

          <div className="ln-hero-col ln-hero-col-brand reveal">
            <div className="ln-eyebrow">For brands</div>
            <h1>
              Get your moments <span className="ln-hl">clipped at scale.</span>
            </h1>
            <p className="ln-sub">
              Fund a campaign, our creator network clips it, you track every verified view.
              You only pay for results.
            </p>
            <button type="button" className="ln-btn-ghost" onClick={() => setDemoOpen(true)}>
              Book a demo
            </button>
          </div>
        </div>
      </header>

      {/* 3 — Proof bar */}
      <section className="ln-proof">
        <div className="ln-proof-grid">
          {PROOF.map((p) => (
            <div key={p.k} className="ln-proof-item reveal">
              <div className="ln-proof-k">{p.k}</div>
              <div className="ln-proof-v">{p.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — Showcase */}
      <section id="showcase" className="ln-sec">
        <div className="ln-wrap">
          <div className="ln-eyebrow reveal">Live in the wild</div>
          <h2 className="ln-h2 reveal">Clips our network already shipped.</h2>
          <p className="ln-lead reveal">
            Anime, music, culture. Real campaign clips, posted by real clippers, with verified
            view counts.
          </p>
          <div className="ln-clip-grid">
            {SHOWCASE.map((c) => (
              <figure key={c.src} className="ln-clip reveal">
                <video
                  src={`/video/campaigns/${c.src}.web.mp4`}
                  poster={`/video/campaigns/${c.src}.poster.jpg`}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <figcaption className="ln-clip-meta">
                  <span className="ln-clip-handle">{c.handle}</span>
                  <span className="ln-clip-views">{c.views}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — How it works */}
      <section id="how" className="ln-sec ln-sec-alt">
        <div className="ln-wrap">
          <div className="ln-eyebrow reveal">How it works</div>
          <h2 className="ln-h2 reveal">Three steps to your first payout.</h2>
          <div className="ln-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="ln-step reveal">
                <div className="ln-step-num">{s.n}</div>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — What you earn */}
      <section id="earn" className="ln-sec">
        <div className="ln-wrap">
          <div className="ln-eyebrow reveal">What you earn</div>
          <h2 className="ln-h2 reveal">You get paid for views. That is it.</h2>
          <ul className="ln-rules reveal">
            <li>Earnings accrue as a balance as your clips rack up verified views.</li>
            <li>Cash out once your balance crosses $100.</li>
            <li>Paid in USD, within 45 days after month-end.</li>
            <li>
              Bot views and fake spikes get caught and do not count. We pay on real views
              only.
            </li>
          </ul>
        </div>
      </section>

      {/* 7 — For brands: book a demo */}
      <section id="book-demo" className="ln-sec ln-brands">
        <div className="ln-wrap">
          <div className="ln-eyebrow reveal">For brands</div>
          <h2 className="ln-h2 reveal">Run a clip campaign.</h2>
          <p className="ln-lead reveal">
            Tell us what you want clipped and your budget. Our creator network clips it, every
            view verified before it is billed. We get back within one business day.
          </p>
          <button
            type="button"
            className="ln-btn-primary ln-btn-on-paper reveal"
            onClick={() => setDemoOpen(true)}
          >
            Book a demo
          </button>
        </div>
      </section>

      {/* 8 — Final CTA + footer */}
      <section className="ln-final">
        <div className="ln-wrap reveal">
          <h2>Start clipping today.</h2>
          <p>Claim your first bounty in two minutes.</p>
          <Link to="/clipper" className="ln-btn-primary">
            Start clipping
          </Link>
        </div>
      </section>

      <footer className="ln-footer">
        <div className="ln-wrap ln-footer-row">
          <span className="ln-footer-mark">orbis studio</span>
          <span>Im Orbis LLC. Every view verified.</span>
        </div>
      </footer>

      <Modal open={demoOpen} onClose={() => setDemoOpen(false)} title="Run a clip campaign">
        <div className="ln-modal-inq">
          <p className="ln-modal-lead">
            Tell us what you want clipped and your budget. Our creator network clips it, every
            view verified before it is billed. We reply within one business day.
          </p>
          <BrandInquiry />
        </div>
      </Modal>
    </>
  );
}
